import { useEffect, useState } from 'react';
import { PageWithTitle } from 'src/Common/components';
import styled from '@emotion/styled';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import useLocalStorageState from 'use-local-storage-state';
import { LiveBadge } from 'src/Common/components/LiveBadge/LiveBadge';
import { ToggleUpdatesButton } from 'src/Common/components/ToggleUpdatesButton/ToggleUpdatesButton';
import { EventAgentRunDrawer } from 'src/Modules/PredictionMapper/components/EventAgentRunDrawer';
import { usePredictions, useLivePredictions } from '../context';
import {
  PredictionsTable,
  CategoryFilter,
  EventAgentRunsButton,
} from '../components';
import { useNotificationsSocket } from '../hooks/useNotificationsSocket';
import { useNotificationSound } from '../hooks/useNotificationSound';

type OpportunitiesTab = 'active' | 'archived' | 'live';

const PredictionsPageContent = () => {
  const {
    opportunities,
    archivedOpportunities,
    liveOpportunities: fallbackLiveOpportunities,
    isConnected,
    count,
    archivedCount,
    liveCount: fallbackLiveCount,
  } = usePredictions();
  const {
    liveOpportunities: dedicatedLiveOpportunities,
    liveCount: dedicatedLiveCount,
    isConnected: dedicatedLiveIsConnected,
    enabled: dedicatedLiveEnabled,
  } = useLivePredictions();

  // Audio cues — same wiring as NotificationsPage. Mounting this hook
  // opens a Socket.io connection to the notifications-websocket service
  // (separate from the prediction-websocket that feeds the table) so the
  // operator hears fills / TNA negatives / errors while watching the
  // Opportunities board. Acknowledging clears `latestEvent` after the
  // sound hook has consumed it so an unrelated re-render doesn't replay.
  const { latestEvent, acknowledge } = useNotificationsSocket();
  useNotificationSound(latestEvent);
  useEffect(() => {
    if (!latestEvent) return;
    acknowledge();
  }, [latestEvent, acknowledge]);

  // Prefer the dedicated live-WS pipeline when it's both configured AND
  // currently connected. In all other cases (env var missing, socket dropped,
  // pipeline scaled to zero) we fall back to the live pairs filtered out of
  // the regular cache — the Live tab keeps working, just without the sub-
  // 100ms latency budget.
  const useDedicatedLive = dedicatedLiveEnabled && dedicatedLiveIsConnected;
  const liveOpportunities = useDedicatedLive
    ? dedicatedLiveOpportunities
    : fallbackLiveOpportunities;
  const liveCount = useDedicatedLive
    ? dedicatedLiveCount
    : fallbackLiveCount;

  const [tab, setTab] = useLocalStorageState<OpportunitiesTab>(
    'V3.Predictions.opportunitiesTab',
    { defaultValue: 'active' }
  );

  // Event-agent run drawer — opens automatically after a fix-match dispatch
  // from a row's unmatch action, AND from the EventAgentRunsButton in the
  // toolbar so the operator can watch any in-flight run on demand.
  const [agentRunId, setAgentRunId] = useState<string | null>(null);

  const data =
    tab === 'archived'
      ? archivedOpportunities
      : tab === 'live'
        ? liveOpportunities
        : opportunities;
  // Only show the loader on the Active tab — the Archived and Live tabs are
  // not "waiting for the first WS payload"; they're empty until a pair is
  // archived or the live classifier flips one.
  const isLoading = tab === 'active' && isConnected && opportunities.length === 0;

  return (
    <PageWithTitle>
      <Stack direction="row">
        <Box style={{ width: '100%' }}>
          <Toolbar>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography
                  variant="h6"
                  color="text.primary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Opportunities
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <CategoryFilter />
                <EventAgentRunsButton onSelect={setAgentRunId} />
                <LiveBadge isLive={isConnected} />
                <ToggleUpdatesButton subsystem="predictions" />
              </Stack>
            </Box>
            <Tabs
              value={tab}
              onChange={(_, v: OpportunitiesTab) => setTab(v)}
              sx={{
                mt: 1,
                minHeight: 32,
                '& .MuiTab-root': {
                  minHeight: 32,
                  py: 0,
                  textTransform: 'none',
                  fontSize: '0.82rem',
                },
                '& .MuiTabs-indicator': { height: 2 },
              }}
            >
              <Tab value="active" label={`Active (${count})`} />
              <Tab value="live" label={`Live (${liveCount})`} />
              <Tab value="archived" label={`Archived (${archivedCount})`} />
            </Tabs>
          </Toolbar>

          <PredictionsTable
            data={data}
            isLoading={isLoading}
            onAgentRunStarted={setAgentRunId}
          />
        </Box>
      </Stack>
      <EventAgentRunDrawer
        runId={agentRunId}
        onClose={() => setAgentRunId(null)}
      />
    </PageWithTitle>
  );
};

export const PredictionsPage = () => {
  return <PredictionsPageContent />;
};

const Toolbar = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%'
});
